import ks from "@shared/kalmstromShared4";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";

declare const Prism: any;

export default class DBOutputSidebar {

    public static ShowOutputSidebar = (title: string, content: string, filename: string, contentType: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.CurrentOutputContent = content;
            DBState.CurrentOutputFilename = filename;
            DBState.CurrentOutputType = contentType;
            DBState.CurrentOutputFiles = null;

            const titleEl: HTMLElement | null = document.getElementById("outputSidebarTitle");
            const codeEl: HTMLElement | null = document.getElementById("outputCode");
            const sidebar: HTMLElement | null = document.getElementById("outputSidebar");
            const singleEl: HTMLElement | null = document.getElementById("outputSidebarSingle");
            const multiEl: HTMLElement | null = document.getElementById("outputSidebarMulti");
            const notesEl: HTMLElement | null = document.getElementById("outputSidebarNotes");
            const downloadAllBtn: HTMLElement | null = document.getElementById("outputDownloadAllBtn");
            const copyBtn: HTMLElement | null = document.getElementById("outputCopyBtn");
            const exportBtn: HTMLElement | null = document.getElementById("outputExportBtn");

            if (titleEl !== null) { titleEl.textContent = title; }
            if (singleEl !== null) { singleEl.style.display = ""; }
            if (multiEl !== null) { multiEl.style.display = "none"; }
            if (notesEl !== null) { notesEl.style.display = "none"; }
            const flowJsonBtn: HTMLElement | null = document.getElementById("outputFlowSaveJsonBtn");
            const flowZipBtn: HTMLElement | null = document.getElementById("outputFlowSaveZipBtn");

            if (downloadAllBtn !== null) { downloadAllBtn.style.display = "none"; }
            if (copyBtn !== null) { copyBtn.style.display = ""; }
            if (exportBtn !== null) { exportBtn.style.display = ""; }
            if (flowJsonBtn !== null) { flowJsonBtn.style.display = "none"; }
            if (flowZipBtn !== null) { flowZipBtn.style.display = "none"; }

            let language: string = "markup";
            if (contentType === "application/json" || filename.endsWith(".json")) {
                language = "json";
            } else if (filename.endsWith(".ps1") || title.indexOf("PowerShell") >= 0) {
                language = "powershell";
            }

            if (codeEl !== null) {
                codeEl.textContent = content;
                codeEl.className = "language-" + language + " line-numbers";
                DBOutputSidebar.ApplyHighlighting(codeEl);
            }

            if (sidebar !== null) {
                sidebar.style.transform = "translateX(0)";
                sidebar.setAttribute("aria-hidden", "false");
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.ShowOutputSidebar");
        }
    };

    public static ShowOutputNotes = (notes: Array<{ list: string; note: string; url?: string }>): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const notesEl: HTMLElement | null = document.getElementById("outputSidebarNotes");
            const notesList: HTMLElement | null = document.getElementById("outputNotesList");
            if (notesEl === null || notesList === null) { return; }

            if (notes.length === 0) {
                notesEl.style.display = "none";
                return;
            }

            notesList.innerHTML = "";

            // Group notes by list, preserving the url field
            const byList: Record<string, Array<{ note: string; url?: string }>> = {};
            notes.forEach((n) => {
                if (!byList[n.list]) { byList[n.list] = []; }
                byList[n.list].push({ note: n.note, url: n.url });
            });

            Object.keys(byList).forEach((listTitle) => {
                const header: HTMLLIElement = document.createElement("li");
                header.className = "notes-list-header";
                header.textContent = listTitle + ":";
                notesList.appendChild(header);
                byList[listTitle].forEach(({ note, url }) => {
                    const li: HTMLLIElement = document.createElement("li");
                    if (url) {
                        li.appendChild(document.createTextNode("\u2022 "));
                        const a: HTMLAnchorElement = document.createElement("a");
                        a.href = url;
                        a.target = "_blank";
                        a.rel = "noopener noreferrer";
                        a.textContent = note;
                        li.appendChild(a);
                    } else {
                        li.textContent = "\u2022 " + note;
                    }
                    notesList.appendChild(li);
                });
            });

            notesEl.style.display = "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.ShowOutputNotes");
        }
    };

    public static ShowMultiOutputSidebar = (
        title: string,
        files: Array<{ name: string; content: string; filename: string; type: string; }>,
        notes: Array<{ list: string; note: string; url?: string }>
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.CurrentOutputFiles = files;
            DBState.CurrentOutputContent = "";

            const titleEl: HTMLElement | null = document.getElementById("outputSidebarTitle");
            const sidebar: HTMLElement | null = document.getElementById("outputSidebar");
            const singleEl: HTMLElement | null = document.getElementById("outputSidebarSingle");
            const multiEl: HTMLElement | null = document.getElementById("outputSidebarMulti");
            const downloadAllBtn: HTMLElement | null = document.getElementById("outputDownloadAllBtn");
            const copyBtn: HTMLElement | null = document.getElementById("outputCopyBtn");
            const exportBtn: HTMLElement | null = document.getElementById("outputExportBtn");

            if (titleEl !== null) { titleEl.textContent = title; }
            if (singleEl !== null) { singleEl.style.display = "none"; }
            if (downloadAllBtn !== null) { downloadAllBtn.style.display = ""; }
            if (copyBtn !== null) { copyBtn.style.display = "none"; }
            if (exportBtn !== null) { exportBtn.style.display = "none"; }

            if (multiEl !== null) {
                multiEl.innerHTML = "";
                files.forEach((file, idx) => {
                    const section: HTMLDivElement = document.createElement("div");
                    section.className = "output-file-section";

                    const header: HTMLDivElement = document.createElement("div");
                    header.className = "output-file-header";
                    header.setAttribute("data-action", "toggle-output-file");
                    header.setAttribute("data-file-index", String(idx));

                    const nameSpan: HTMLSpanElement = document.createElement("span");
                    nameSpan.className = "output-file-name";
                    nameSpan.textContent = file.filename;

                    const toggleSpan: HTMLSpanElement = document.createElement("span");
                    toggleSpan.className = "output-file-toggle";
                    toggleSpan.textContent = idx === 0 ? "\u25BC" : "\u25B6";

                    const dlBtn: HTMLButtonElement = document.createElement("button");
                    dlBtn.className = "toolbar-btn secondary";
                    dlBtn.setAttribute("data-action", "download-single-output");
                    dlBtn.setAttribute("data-file-index", String(idx));
                    dlBtn.style.fontSize = "11px";
                    dlBtn.style.padding = "4px 8px";
                    dlBtn.textContent = "Save";

                    header.appendChild(nameSpan);
                    header.appendChild(toggleSpan);
                    header.appendChild(dlBtn);

                    const body: HTMLDivElement = document.createElement("div");
                    body.className = "output-file-body" + (idx === 0 ? "" : " collapsed");
                    body.id = "outputFileBody" + idx;

                    const pre: HTMLPreElement = document.createElement("pre");
                    pre.className = "output-code language-json line-numbers";
                    pre.textContent = file.content;
                    DBOutputSidebar.ApplyHighlighting(pre);

                    body.appendChild(pre);
                    section.appendChild(header);
                    section.appendChild(body);
                    multiEl.appendChild(section);
                });
                multiEl.style.display = "";
            }

            DBOutputSidebar.ShowOutputNotes(notes);

            if (sidebar !== null) {
                sidebar.style.transform = "translateX(0)";
                sidebar.setAttribute("aria-hidden", "false");
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.ShowMultiOutputSidebar");
        }
    };

    public static ToggleOutputFile = (index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const body: HTMLElement | null = document.getElementById("outputFileBody" + index);
            const multiEl: HTMLElement | null = document.getElementById("outputSidebarMulti");
            if (body === null || multiEl === null) { return; }

            const isCollapsed: boolean = body.classList.contains("collapsed");
            if (isCollapsed) {
                body.classList.remove("collapsed");
            } else {
                body.classList.add("collapsed");
            }

            // Update toggle arrow on the header for this section
            const sections = multiEl.querySelectorAll(".output-file-section");
            if (sections[index]) {
                const toggle: HTMLElement | null = sections[index].querySelector(".output-file-toggle");
                if (toggle !== null) {
                    toggle.textContent = isCollapsed ? "\u25BC" : "\u25B6";
                }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.ToggleOutputFile");
        }
    };

    public static DownloadSingleOutput = (index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.CurrentOutputFiles === null) { return; }
            const file = DBState.CurrentOutputFiles[index];
            if (!file) { return; }
            DBOutputSidebar.TriggerDownload(file.content, file.filename, file.type);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.DownloadSingleOutput");
        }
    };

    public static DownloadAllOutputs = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.CurrentOutputFiles === null || DBState.CurrentOutputFiles.length === 0) { return; }
            DBState.CurrentOutputFiles.forEach((file, idx) => {
                setTimeout(() => {
                    DBOutputSidebar.TriggerDownload(file.content, file.filename, file.type);
                }, idx * 100);
            });
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.DownloadAllOutputs");
        }
    };

    private static TriggerDownload = (content: string, filename: string, type: string): void => {
        const blob: Blob = new Blob([content], { type });
        const url: string = URL.createObjectURL(blob);
        const a: HTMLAnchorElement = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    private static ApplyHighlighting = (codeEl: HTMLElement): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (typeof Prism !== "undefined") {
                Prism.highlightElement(codeEl);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.ApplyHighlighting");
        }
    };

    public static ShowFlowExportButtons = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const exportBtn: HTMLElement | null = document.getElementById("outputExportBtn");
            const flowJsonBtn: HTMLElement | null = document.getElementById("outputFlowSaveJsonBtn");
            const flowZipBtn: HTMLElement | null = document.getElementById("outputFlowSaveZipBtn");
            if (exportBtn !== null) { exportBtn.style.display = "none"; }
            if (flowJsonBtn !== null) { flowJsonBtn.style.display = ""; }
            if (flowZipBtn !== null) { flowZipBtn.style.display = ""; }
        } catch (e) { ks.kGlobalErrorHandler(e, "DBOutputSidebar.ShowFlowExportButtons"); }
    };

    public static CloseOutputSidebar = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const sidebar: HTMLElement | null = document.getElementById("outputSidebar");
            if (sidebar !== null) {
                sidebar.style.transform = "translateX(100%)";
                sidebar.setAttribute("aria-hidden", "true");
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.CloseOutputSidebar");
        }
    };

    public static CopyOutput = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (!DBState.CurrentOutputContent) {
                DBModalManager.ShowToast("No content to copy", "error");
                return;
            }

            const OnCopySuccess = (): void => {
                DBModalManager.ShowToast("Copied to clipboard", "success");
            };
            const OnCopyError = (): void => {
                DBModalManager.ShowToast("Failed to copy to clipboard", "error");
            };
            navigator.clipboard.writeText(DBState.CurrentOutputContent).then(OnCopySuccess).catch(OnCopyError);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.CopyOutput");
        }
    };

    public static ExportOutput = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (!DBState.CurrentOutputContent) {
                DBModalManager.ShowToast("No content to export", "error");
                return;
            }

            const blob: Blob = new Blob([DBState.CurrentOutputContent], { type: DBState.CurrentOutputType });
            const url: string = URL.createObjectURL(blob);
            const a: HTMLAnchorElement = document.createElement("a");
            a.href = url;
            a.download = DBState.CurrentOutputFilename;
            a.click();
            URL.revokeObjectURL(url);
            DBModalManager.ShowToast("File exported", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBOutputSidebar.ExportOutput");
        }
    };
}
